import { RootState, AppThunk } from "ducks/state";
import { newNotification } from "./notification";
import { hen, Hen } from "@udok/lib/internal/store";
import {
  RecordFlag,
  PublicRecordFlagFilter,
  PatientRecordFlag,
} from "@udok/lib/api/models";
import {
  fetchPublicRecordFlags,
  createPatientRecordFlag,
} from "@udok/lib/api/patient";
import { getToken, UNAUTHORIZED } from "ducks/auth";
import { createSelector } from "reselect";

export type InitialState = {
  recordFlagByID: { [reflID: number]: RecordFlag | undefined };
  patientRecordFlagByPatiID: {
    [patiID: string]: PatientRecordFlag[] | undefined;
  };
};

const initialState: InitialState = {
  recordFlagByID: {},
  patientRecordFlagByPatiID: {},
};

class RecordFlagSlice extends Hen<InitialState> {
  recordFlagsLoaded(flags: RecordFlag[]) {
    flags.forEach((flag) => {
      this.state.recordFlagByID[flag.reflID] = flag;
    });
  }
  recordFlagLoaded(flag: RecordFlag) {
    this.state.recordFlagByID[flag.reflID] = flag;
  }
  recordFlagRemoved(flag: RecordFlag) {
    delete this.state.recordFlagByID[flag.reflID];
  }
  patientRecordFlagLoaded(flag: PatientRecordFlag) {
    let patFlags = [
      ...(this.state.patientRecordFlagByPatiID[flag.patiID] ?? []),
    ];
    const index = patFlags.findIndex((pf) => pf.parfID === flag.parfID);
    if (index === -1) {
      patFlags = [...patFlags, flag];
    } else {
      patFlags[index] = flag;
    }
    this.state.patientRecordFlagByPatiID[flag.patiID] = patFlags;
  }
  patientRecordFlagsLoaded(flags: PatientRecordFlag[]) {
    let flagByPatiID: { [patiID: string]: PatientRecordFlag[] } = {};
    flags.forEach((f) => {
      flagByPatiID[f.patiID] = [...(flagByPatiID[f.patiID] ?? []), f];
    });
    Object.keys(flagByPatiID).forEach((patiID) => {
      this.state.patientRecordFlagByPatiID[patiID] = flagByPatiID[patiID];
    });
  }
  patientRecordFlagDelete(flag: PatientRecordFlag) {
    let patFlags = [
      ...(this.state.patientRecordFlagByPatiID[flag.patiID] ?? []),
    ];
    const index = patFlags.findIndex((pf) => pf.parfID === flag.parfID);
    if (index !== -1) {
      patFlags.splice(index, 1);
    }
    this.state.patientRecordFlagByPatiID[flag.patiID] = [...patFlags];
  }
}

export const [Reducer, actions] = hen(new RecordFlagSlice(initialState), {
  [UNAUTHORIZED]: () => {
    return initialState;
  },
});

const recordFlagByIDSelector = (state: RootState) =>
  state.recordFlag.recordFlagByID;

export const getRecordFlags = createSelector(
  [recordFlagByIDSelector],
  (recordFlagByID) => {
    return {
      list: Object.values(recordFlagByID).filter(Boolean) as RecordFlag[],
    };
  }
);

export function createOnePatientRecordFlag(data: {
  patiID?: string;
  doctID?: string;
  clinID?: string;
  reflID: number;
}): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return createPatientRecordFlag(apiToken, data)
      .then((r) => {
        dispatch(actions.patientRecordFlagLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function loadPublicRecordFlags(
  filter?: PublicRecordFlagFilter
): AppThunk<Promise<void>> {
  return async (dispatch) => {
    return fetchPublicRecordFlags(filter)
      .then((r) => {
        dispatch(actions.recordFlagsLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}
